/**
 * Core game type definitions
 */

import type { HexCoord } from '../HexUtils';

// Game phases
export enum Phase {
  COLLECT = 0,
  BUILD = 1,
  MOVE = 2,
  BATTLE = 3,
}

// Game modes
export enum GameMode {
  CONQUEST = 0,
  CAPTURE_RELIC = 1,
}

// Unit types
export enum UnitType {
  SCOUT = 'scout',
  FIGHTER = 'fighter',
  CRUISER = 'cruiser',
  SPACE_TUG = 'space_tug',
  ALIEN_LEVIATHAN = 'alien_leviathan',
}

// Re-export BuildingType from shared common
import { BuildingType } from '../common/BuildingType';
export { BuildingType } from '../common/BuildingType';

// AI difficulty levels
export enum AIDifficulty {
  EASY = 0,
  NORMAL = 1,
  HARD = 2,
}

// Component/equipment types
export enum ComponentType {
  WEAPON = 'weapon',
  ENGINE = 'engine',
  SHIELD = 'shield',
  SENSOR = 'sensor',
  ARMOR = 'armor',
}

// Tech tree paths
export enum TechPath {
  WEAPONS = 'weapons',
  ARMOR = 'armor',
  SPEED = 'speed',
}

// Game configuration
export interface GameConfig {
  aiCount: number;
  aiDifficulty?: AIDifficulty; // Single difficulty for all AI
  aiDifficulties?: AIDifficulty[]; // Per-AI difficulties
  planetCount: number;
  mapSize: number;
  gameMode: GameMode;
  activeNeutrals?: boolean;
}

// Player state
export interface PlayerState {
  id: number;
  name: string;
  credits: number;
  isAI: boolean;
  techLevels: Record<TechPath, number>;
  color: number;
}

// Unit stats (base values)
export interface UnitStats {
  hp: number;
  shields: number;
  attack: number;
  defense: number;
  speed: number;
  attackRange: number;
  componentSlots: number;
  cost: number;
  cargoCapacity: number;
  miningEfficiency: number;
}

// Building costs
export interface BuildingCost {
  type: BuildingType;
  credits: number;
}

// Unit state
export interface UnitState {
  id: string;
  type: UnitType;
  ownerId: number;
  position: HexCoord;
  hp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  movesRemaining: number;
  hasAttacked: boolean;
  fleetId: number | null;
  isFleetLeader: boolean;
  cargo: number;
  maxCargo: number;
  isTowing: boolean;
  towedEntityId: string | null;
}

// Planet state
export interface PlanetState {
  id: string;
  name: string;
  ownerId: number;
  position: HexCoord;
  hp: number;
  maxHp: number;
  shields: number;
  maxShields: number;
  buildings: BuildingType[];
  maxBuildingSlots: number;
  isHomePlanet: boolean;
}

// Asteroid state
export interface AsteroidState {
  id: string;
  ownerId: number;
  position: HexCoord;
  miningLevel: number;
  storedResources: number;
  maxStorage: number;
  resourceRichness: number;
}

// Damage result from combat
export interface DamageResult {
  hit: boolean;
  shieldDamage: number;
  hullDamage: number;
  destroyed: boolean;
  cargoLooted: number;
}

// Attack result
export interface AttackResult {
  success: boolean;
  hit: boolean;
  damageDealt: number;
  shieldsAbsorbed: number;
  targetDestroyed: boolean;
  cargoLooted: number;
  targetPosition: HexCoord;
}

// Network message types
export interface NetworkMessage {
  type: string;
  payload: unknown;
  timestamp: number;
}

// Game action for network sync
export interface GameAction {
  actionType: 'move' | 'attack' | 'build_unit' | 'build_building' | 'tech_choice' | 'end_phase';
  entityId?: string;
  targetId?: string;
  position?: HexCoord;
  data?: unknown;
}
